import { useEffect, useState } from 'react';
import { avatarUrl } from '../lib/api';

interface Props {
  userId: string;
  fullName: string;
  hasAvatar: boolean;
  /** Kelas ukuran Tailwind, mis. `h-7 w-7`. Ukurannya urusan pemanggil. */
  size?: string;
  /**
   * Penanda versi gambar (`user.avatarVersion`). Ikut ke URL supaya avatar yang
   * baru diganti langsung terlihat, dan supaya yang tidak berubah tetap boleh
   * di-cache lama.
   */
  versi?: string | number | null;
}

/**
 * Foto profil, dengan inisial sebagai jaring pengaman.
 *
 * Tiga keadaan, bukan dua: belum punya avatar, punya tapi gambarnya gagal
 * dimuat, dan berhasil. Yang kedua paling gampang terlewat — avatar yang baru
 * dihapus di tab lain, atau permintaan yang kena 404 setelah user dihapus —
 * dan tanpa `onError` hasilnya ikon gambar rusak bawaan browser di tengah
 * header, yang terbaca sebagai aplikasi yang rusak.
 *
 * `aria-hidden` pada kedua wujudnya disengaja: nama orangnya selalu sudah
 * tertulis di sebelahnya. Avatar yang ikut dibacakan hanya membuat pembaca
 * layar menyebut nama yang sama dua kali.
 */
export function Avatar({ userId, fullName, hasAvatar, size = 'h-7 w-7', versi }: Props) {
  const [gagal, setGagal] = useState(false);

  // Gambar baru berhak dicoba lagi. Tanpa reset ini, satu kali gagal memuat
  // membuat inisial menempel selamanya — termasuk sesudah orangnya mengunggah
  // foto pengganti, yang persis saat dia paling ingin melihat perubahannya.
  useEffect(() => setGagal(false), [userId, hasAvatar, versi]);

  const src = gagal ? null : avatarUrl(userId, hasAvatar, versi);

  if (!src) {
    return (
      <span
        aria-hidden="true"
        className={`flex shrink-0 items-center justify-center rounded-full bg-subtle font-semibold ${size}`}
      >
        {fullName.slice(0, 1).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      onError={() => setGagal(true)}
      // `object-cover` cuma pengaman: server sudah menyimpannya persegi. Yang
      // benar-benar dibutuhkan adalah cincin tipis — foto berlatar terang di
      // atas permukaan terang kehilangan tepinya tanpa itu.
      className={`shrink-0 rounded-full object-cover ring-1 ring-line ${size}`}
    />
  );
}
